import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useEffect, useMemo } from 'react'
import { batch } from 'react-redux'

import EmptyState from '@/components/EmptyState'
import Pagination from '@/modules/media/components/Pagination'
import JdihCard from '@/modules/public-service/jdih/components/JdihCard'
import { appSetIsLoading } from '@/reducers/app'
import {
  jdihSetIsEventFilter,
  jdihSetIsEventFilterFirstTime,
  jdihSetKeyword,
  jdihSetSelected,
  jdihSetSkip,
  jdihSetTypeXid,
  jdihSetYear,
} from '@/reducers/jdih'
import { getJdihCategoriesList, useLazyGetJdihListQuery } from '@/services/jdih'
import clsxm from '@/utils/clsxm'
import { useAppDispatch, useAppSelector } from '@/utils/hooks'

import EmptyStateImage from '~/assets/images/jdih/jdih-empty-state.webp'
import NotFoundStateImage from '~/assets/images/jdih/jdih-not-found-state.webp'
const JdihContent = () => {
  const router = useRouter()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const jdihIsEventFilter = useAppSelector((state) => state.Jdih.isEventFilter)
  const jdihIsEventFilterFirstTime = useAppSelector((state) => state.Jdih.isEventFilterFirstTime)
  const jdihKeyword = useAppSelector((state) => state.Jdih.keyword)
  const jdihSelected = useAppSelector((state) => state.Jdih.selected)
  const jdihSkip = useAppSelector((state) => state.Jdih.skip)
  const jdihLimit = useAppSelector((state) => state.Jdih.limit)
  const jdihTypeXid = useAppSelector((state) => state.Jdih.typeXid)
  const jdihYear = useAppSelector((state) => state.Jdih.year)

  const [getJdihList, responseJdihList] = useLazyGetJdihListQuery()
  const [getJdihHighlight, responseJdihHighLight] = useLazyGetJdihListQuery()
  const { data: responseJdihCategories } = useAppSelector(
    getJdihCategoriesList.select({
      params: { skip: 0, limit: 1000 },
      headers: { 'Accept-Language': String(router.locale) },
    })
  )
  const jdihCategories = useMemo(() => {
    if (responseJdihCategories && responseJdihCategories?.data.items) {
      const items = [...responseJdihCategories.data.items]
      items.unshift({
        xid: '',
        name: String(t('common:all')),
        createdAt: 0,
        updatedAt: 0,
        version: 0,
      })
      return items
    }
    return []
  }, [responseJdihCategories])

  const handlePageClick = (event: { selected: number }) => {
    const newOffset = (event.selected * jdihLimit) % Number(responseJdihList?.data?.data.metadata.total)
    batch(() => {
      dispatch(jdihSetSelected(event.selected))
      dispatch(jdihSetSkip(newOffset))
      dispatch(jdihSetIsEventFilter(true))
      dispatch(jdihSetIsEventFilterFirstTime(true))
    })
  }

  const fetchJdihHighlight = () => {
    getJdihHighlight({
      params: { skip: 0, limit: 3, sortBy: 'latest' },
      headers: { 'Accept-Language': String(router.locale) },
    })
  }
  const fetchJdih = () => {
    const params: any = {}
    if (jdihKeyword.length > 0) {
      params.keyword = jdihKeyword
    }
    if (jdihYear.length > 0) {
      params.year = jdihYear
    }
    if (jdihTypeXid) {
      params.typeXid = jdihTypeXid
    }

    getJdihList({
      params: {
        skip: jdihSkip,
        limit: jdihLimit,
        ...params,
      },
      headers: { 'Accept-Language': String(router.locale) },
    })
  }
  const handleChangeTab = (xid: string) => {
    batch(() => {
      dispatch(jdihSetTypeXid(xid))
      dispatch(jdihSetIsEventFilter(true))
      dispatch(jdihSetSkip(0))
      dispatch(jdihSetSelected(0))
      dispatch(jdihSetIsEventFilterFirstTime(true))
    })
  }
  const handleReset = () => {
    batch(() => {
      dispatch(jdihSetIsEventFilter(true))
      dispatch(jdihSetSkip(0))
      dispatch(jdihSetSelected(0))
      dispatch(jdihSetYear(''))
      dispatch(jdihSetKeyword(''))
    })
  }

  useEffect(() => {
    fetchJdihHighlight()
    fetchJdih()
  }, [])

  useEffect(() => {
    if (jdihIsEventFilter) {
      dispatch(jdihSetIsEventFilter(false))
    }
    dispatch(appSetIsLoading(responseJdihList.isFetching))
  }, [responseJdihList])

  useEffect(() => {
    if (jdihIsEventFilter) {
      fetchJdih()
    }
  }, [jdihIsEventFilter, jdihKeyword, jdihSelected, jdihTypeXid, jdihYear])

  useEffect(() => {
    if (jdihCategories && jdihCategories.length > 0 && !jdihTypeXid.length) {
      dispatch(jdihSetTypeXid(jdihCategories[0].xid))
    }
  }, [jdihCategories, jdihIsEventFilter, jdihTypeXid])

  return (
    <section className='container flex w-full flex-col gap-6 pb-8 pt-4 lg:py-8 lg:pb-25'>
      <>
        {!jdihIsEventFilterFirstTime &&
          responseJdihHighLight?.data &&
          responseJdihHighLight?.data.data.items.length > 0 && (
            <>
              <h5 className='font-bold leading-normal'>{t('jdih:content.latestRegulations')}</h5>
              {responseJdihHighLight?.data.data.items.map((item) => (
                <JdihCard
                  key={item.xid}
                  data={item}
                  isNew
                />
              ))}
            </>
          )}

        <div className='flex h-[54px] overflow-auto rounded-t-lg lg:container lg:h-[74px] lg:overflow-hidden lg:px-0 lg:hover:overflow-auto'>
          {jdihCategories.length > 0 &&
            jdihCategories.map((tab) => (
              <button
                key={tab.xid}
                className={clsxm(
                  'flex h-full items-center justify-center border-b-[2px] border-b-white px-6 py-4 text-black transition-all duration-300',
                  tab.xid == jdihTypeXid && 'border-b-red font-bold text-red'
                )}
                onClick={() => handleChangeTab(tab.xid)}
              >
                <span
                  className={clsxm(
                    'whitespace-nowrap text-xs font-medium text-black text-inherit lg:text-sm lg:tracking-[=0.176px]',
                    tab.xid == jdihTypeXid && 'font-bold tracking-normal'
                  )}
                >
                  {tab.name}
                </span>
              </button>
            ))}
        </div>
        {Number(responseJdihList?.data?.data.metadata.total ?? 0) > 0 && (jdihKeyword.length || jdihYear.length) ? (
          <div
            dangerouslySetInnerHTML={{
              __html: String(t('jdih:found', { found: responseJdihList?.data?.data.metadata.total })),
            }}
          />
        ) : null}
        <>
          {responseJdihList?.data &&
            responseJdihList?.data.data.items.map((item) => (
              <JdihCard
                key={item.xid}
                data={item}
                isNew={false}
              />
            ))}
          <Pagination
            onPageChange={handlePageClick}
            selected={jdihSelected}
            perPage={10}
            totalData={responseJdihList?.data?.data.metadata.total ?? 0}
          />
        </>
      </>
      <>
        {jdihKeyword.length === 0 && responseJdihList?.data && responseJdihList?.data.data.items.length == 0 && (
          <EmptyState
            img={EmptyStateImage.src}
            title={t('jdih:emptyState.title')}
            description={t('jdih:emptyState.description')}
            imgClass='mx-auto w-[250px] h-[200px] lg:w-[500px] lg:h-[400px]'
            containerClass='self-center mt-[34px] mb-[50px] lg:mb-[100px]'
          />
        )}
        {jdihKeyword.length > 0 && responseJdihList?.data && responseJdihList?.data.data.items.length === 0 && (
          <EmptyState
            img={NotFoundStateImage.src}
            title={t('jdih:notFoundState.title')}
            description={t('jdih:notFoundState.description')}
            imgClass='mx-auto w-[250px] h-[200px] lg:w-[500px] lg:h-[400px]'
            containerClass='self-center mt-[34px] mb-[50px] lg:mb-[100px]'
            buttonText={t('common:backTo', { page: 'JDIH' }) || undefined}
            onClick={handleReset}
          />
        )}
      </>
    </section>
  )
}

export default JdihContent
